import { Link } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";
import { useKit } from "@/context/KitContext";

export const KitItemRow = ({ item, onRemove }) => (
  <li className="flex items-center gap-3 py-3" data-testid="kit-item">
    <img src={item.image} alt="" className="w-12 h-12 rounded-lg object-cover bg-slate-100" />
    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-slate-900 truncate">{item.name}</p>
      <p className="text-xs text-slate-500">{[item.color, item.size].filter(Boolean).join(" · ") || "—"}</p>
    </div>
    <span className="font-mono text-xs text-slate-600">{item.points}</span>
    {onRemove && (
      <button onClick={onRemove} data-testid="kit-item-remove-btn" className="p-1 rounded-full text-slate-400 hover:text-[#EA4335] hover:bg-red-50" aria-label="Remove">
        <X className="w-4 h-4" />
      </button>
    )}
  </li>
);

const Meter = ({ total, budget }) => {
  const pct = budget ? Math.min(100, (total / budget) * 100) : 0;
  const color = pct >= 90 ? "bg-[#34A853]" : pct >= 65 ? "bg-[#FBBC04]" : "bg-[#1A73E8]";
  return (
    <div data-testid="points-meter">
      <div className="flex justify-between text-xs font-mono uppercase tracking-widest text-slate-400 mb-2">
        <span>Points used</span>
        <span className="text-slate-900" data-testid="points-meter-value">{total} / {budget}</span>
      </div>
      <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${color} transition-[width] duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <p className="mt-2 text-xs text-slate-500" data-testid="points-remaining">{budget - total} points left</p>
    </div>
  );
};

export const KitPanel = ({ maxItems, budget }) => {
  const { items, remove, total } = useKit();
  return (
    <aside className="bg-white border border-slate-200 rounded-2xl p-6 lg:sticky lg:top-24 space-y-6" data-testid="kit-panel">
      <div className="flex items-baseline justify-between">
        <h2 className="font-heading text-2xl font-bold tracking-tight">Your kit</h2>
        <span className="font-mono text-sm text-slate-500" data-testid="kit-count">{items.length}/{maxItems} items</span>
      </div>
      <div className="flex gap-1.5" aria-hidden>
        {Array.from({ length: maxItems }).map((_, i) => (
          <span key={i} className={`h-1.5 flex-1 rounded-full ${i < items.length ? ["bg-[#1A73E8]", "bg-[#EA4335]", "bg-[#FBBC04]", "bg-[#34A853]"][i % 4] : "bg-slate-100"}`} />
        ))}
      </div>
      <Meter total={total} budget={budget} />
      {items.length === 0 ? (
        <p className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-xl" data-testid="kit-empty">
          Your kit is empty. Add up to {maxItems} items.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {items.map((i) => <KitItemRow key={i.key} item={i} onRemove={() => remove(i.key)} />)}
        </ul>
      )}
      <Link
        to={items.length ? "/checkout" : "#"} data-testid="kit-checkout-btn" aria-disabled={!items.length}
        className={`h-12 rounded-full font-semibold flex items-center justify-center gap-2 transition-colors ${
          items.length ? "bg-[#1A73E8] hover:bg-[#1557B0] text-white" : "bg-slate-100 text-slate-400 pointer-events-none"
        }`}
      >
        Continue to delivery <ArrowRight className="w-4 h-4" />
      </Link>
    </aside>
  );
};
